import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";
import { RootLayout } from "@/components/layout/RootLayout";
import { useAuth } from "@/context/AuthContext";
import { Helmet } from "react-helmet";

/**
 * Renders the Privacy Policy page for TellerWand.
 * @returns {JSX.Element} A JSX Element containing the Privacy Policy content and layout.
 */
export default function Privacy() {
  const { user } = useAuth();

  const privacyContent = (
    <Card className="mb-6">
      <CardContent className="prose dark:prose-invert max-w-none p-3 sm:p-4 md:p-6">
        <p className="text-sm sm:text-base md:text-lg font-medium">Last updated: January 5, 2026</p>

        <p className="mt-4">
          TellerWand Inc. ("TellerWand," "we," "us," or "our") respects your privacy and is
          committed to protecting your personal information. This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information when you use our website, mobile
          application, and services (collectively, the "Service").
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">1. Introduction</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">1.1 Scope</h3>
        <p>This Privacy Policy applies to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>All users of the TellerWand Service</li>
          <li>Visitors to our website (https://tellerwand.com)</li>
          <li>Users of our mobile application and Progressive Web App (PWA)</li>
          <li>All data collected through our Service</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">1.2 Compliance</h3>
        <p>This Privacy Policy has been updated to comply with:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>GDPR (General Data Protection Regulation)</strong> - European Union
          </li>
          <li>
            <strong>CCPA (California Consumer Privacy Act)</strong> - California, United States
          </li>
          <li>
            <strong>DPDPA (Digital Personal Data Protection Act)</strong> - India
          </li>
          <li>
            <strong>ISO 27001</strong> - Information Security Management
          </li>
          <li>
            <strong>SOC 2</strong> - Security, Availability, and Confidentiality
          </li>
          <li>Other applicable data protection regulations</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">1.3 Acceptance</h3>
        <p>
          By using our Service, you consent to the collection, use, and disclosure of your
          information as described in this Privacy Policy. If you do not agree with the terms of
          this Privacy Policy, please do not access or use our Service.
        </p>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">2. Information We Collect</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">
          2.0 Data Minimization and Purpose Limitation
        </h3>
        <p>We follow the principles of data minimization and purpose limitation:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Data Minimization:</strong> We only collect information that is necessary for
            providing our Service
          </li>
          <li>
            <strong>Purpose Limitation:</strong> We use your information only for the purposes
            stated in this Privacy Policy
          </li>
          <li>
            <strong>Data Accuracy:</strong> You are responsible for ensuring the accuracy of
            information you provide
          </li>
          <li>
            <strong>Storage Limitation:</strong> We retain data only for as long as necessary for
            the stated purposes
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">2.1 Information You Provide Directly</h3>
        <p>We collect information that you provide directly to us, including:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">2.1.1 Account Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Full name</li>
          <li>Email address</li>
          <li>Phone number (optional)</li>
          <li>Password (encrypted)</li>
          <li>Profile picture (optional)</li>
          <li>Date of birth (optional)</li>
          <li>Location (optional)</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.1.2 Financial Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            Investment details (mutual funds, stocks, PPF, EPF, NPS, real estate, gold, fixed
            deposits, bonds, etc.)
          </li>
          <li>Transaction records (income and expenses)</li>
          <li>Financial goals and targets</li>
          <li>Asset valuations</li>
          <li>Portfolio information</li>
          <li>Bank account details (if provided for payment processing)</li>
          <li>Payment method information (for subscription payments)</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">
          2.1.3 Family Information (Premium Feature)
        </h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Family member names and profiles</li>
          <li>Family financial data</li>
          <li>Family goals and planning information</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.1.4 Communication Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Support requests and inquiries</li>
          <li>Feedback and survey responses</li>
          <li>Blog comments and submissions</li>
          <li>Referral program information</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">2.2 Information Collected Automatically</h3>
        <p>We automatically collect certain information when you use our Service:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">2.2.1 Device Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Device type (mobile, tablet, desktop)</li>
          <li>Operating system</li>
          <li>Browser type and version</li>
          <li>Device identifiers</li>
          <li>IP address</li>
          <li>Screen resolution</li>
          <li>Language preferences</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.2.2 Usage Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Pages visited and time spent</li>
          <li>Features used and frequency</li>
          <li>Click patterns and navigation paths</li>
          <li>Search queries</li>
          <li>Date and time of access</li>
          <li>Session duration</li>
          <li>Error logs and crash reports</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.2.3 Location Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>General location (country, city) based on IP address</li>
          <li>Precise location (if you grant permission)</li>
          <li>Time zone</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.2.4 Technical Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Log files</li>
          <li>Performance metrics</li>
          <li>System configuration</li>
          <li>Network information</li>
          <li>Cookie and tracking data</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">2.3 Information from Third Parties</h3>
        <p>We may receive information about you from third-party sources:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">2.3.1 Authentication Providers</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Information from social login providers (Google, Apple, etc.)</li>
          <li>OAuth provider data</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.3.2 Payment Processors</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Payment transaction details</li>
          <li>Billing information</li>
          <li>Subscription status</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.3.3 Analytics Services</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Aggregated usage statistics</li>
          <li>User behavior insights</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.3.4 Referral Program</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Referral link clicks</li>
          <li>Referrer information</li>
          <li>Conversion data</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">2.4 Cookies and Tracking Technologies</h3>
        <p>
          We use cookies, web beacons, and similar tracking technologies to collect information:
        </p>

        <h4 className="text-base font-semibold mt-3 mb-2">2.4.1 Types of Cookies</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Essential Cookies:</strong> Required for Service functionality
          </li>
          <li>
            <strong>Analytics Cookies:</strong> Help us understand how users interact with our
            Service
          </li>
          <li>
            <strong>Preference Cookies:</strong> Remember your settings and preferences
          </li>
          <li>
            <strong>Marketing Cookies:</strong> Used for advertising and referral tracking
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.4.2 Third-Party Cookies</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Google Analytics</li>
          <li>Cloudflare (security and performance)</li>
          <li>Payment processors</li>
          <li>Other service providers</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">2.5 Mobile App Permissions</h3>
        <p>If you use our mobile application, we may request the following permissions:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">2.5.1 Camera Access</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Purpose:</strong> To capture photos of receipts and financial documents
          </li>
          <li>
            <strong>When Used:</strong> Only when you explicitly choose to take a photo
          </li>
          <li>
            <strong>Storage:</strong> Photos are stored securely and can be deleted by you
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.5.2 Photo Library Access</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Purpose:</strong> To access and share receipts, documents, and images
          </li>
          <li>
            <strong>When Used:</strong> Only when you choose to select an image from your library
          </li>
          <li>
            <strong>Storage:</strong> Images are processed and stored securely
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.5.3 Storage Permissions</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Purpose:</strong> To save exported reports, documents, and cached data
          </li>
          <li>
            <strong>When Used:</strong> For data export and offline functionality
          </li>
          <li>
            <strong>Storage:</strong> Files are stored in app-specific directories
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.5.4 Network Permissions</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Purpose:</strong> To connect to our servers and sync data
          </li>
          <li>
            <strong>When Used:</strong> Required for Service functionality
          </li>
          <li>
            <strong>Data:</strong> Only Service-related data is transmitted
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">2.6 Device Fingerprinting</h3>
        <p>We may collect device fingerprinting information for:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Fraud Prevention:</strong> Detecting and preventing fraudulent activities
          </li>
          <li>
            <strong>Security:</strong> Identifying suspicious login attempts
          </li>
          <li>
            <strong>Account Protection:</strong> Securing your account from unauthorized access
          </li>
        </ul>
        <p>Device fingerprinting may include:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Device identifiers</li>
          <li>Browser characteristics</li>
          <li>Operating system information</li>
          <li>Network information</li>
          <li>Hardware characteristics</li>
        </ul>
        <p>This information is used solely for security and fraud prevention purposes.</p>

        <h3 className="text-lg font-semibold mt-4 mb-2">2.7 Public Information</h3>
        <p>Some information you provide may be publicly visible:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">2.7.1 Blog Posts and Comments</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Blog posts you publish are publicly accessible</li>
          <li>Comments on blog posts are publicly visible</li>
          <li>Your username or display name may be associated with public content</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">2.7.2 Shared Content</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Content you choose to share publicly</li>
          <li>Referral program participation (if made public)</li>
          <li>Public profile information (if you enable public profile)</li>
        </ul>
        <p>You can control what information is made public through your account settings.</p>

        {/* PART 2: Sections 3-5 - How We Use/Share Information, Data Security */}
        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">3. How We Use Your Information</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.1 Service Provision</h3>
        <p>We use your information to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Provide, maintain, and improve our Service</li>
          <li>Process transactions and manage subscriptions</li>
          <li>Authenticate and authorize access</li>
          <li>Personalize your experience</li>
          <li>Enable features and functionality</li>
          <li>Process payments and billing</li>
          <li>Send service-related communications</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.2 Communication</h3>
        <p>We use your information to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Respond to your inquiries and support requests</li>
          <li>Send important service updates and notifications</li>
          <li>Provide customer support</li>
          <li>Send marketing communications (with your consent)</li>
          <li>Notify you about changes to our Service or policies</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.3 Analytics and Improvement</h3>
        <p>We use your information to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Analyze usage patterns and trends</li>
          <li>Improve Service performance and reliability</li>
          <li>Develop new features and functionality</li>
          <li>Conduct research and analytics</li>
          <li>Monitor and prevent fraud and abuse</li>
          <li>Debug and fix technical issues</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.4 Legal and Compliance</h3>
        <p>We use your information to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Comply with legal obligations</li>
          <li>Respond to legal requests and court orders</li>
          <li>Enforce our Terms of Service</li>
          <li>Protect our rights and property</li>
          <li>Prevent fraud and security threats</li>
          <li>Comply with regulatory requirements</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.5 Business Operations</h3>
        <p>We use your information to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Manage our business operations</li>
          <li>Process referrals and rewards</li>
          <li>Conduct business analytics</li>
          <li>Improve customer experience</li>
          <li>Develop business strategies</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.6 AI and Machine Learning</h3>
        <p>We may use your information for AI and machine learning purposes:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">3.6.1 Smart Recommendations</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Provide personalized investment recommendations</li>
          <li>Suggest financial goals based on your profile</li>
          <li>Recommend expense categories and budgeting strategies</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">3.6.2 Automated Insights</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Generate automated financial insights</li>
          <li>Analyze spending patterns</li>
          <li>Identify opportunities for savings and optimization</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">3.6.3 Fraud Detection</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Detect fraudulent activities using automated systems</li>
          <li>Assess risk scores for transactions and referrals</li>
          <li>Prevent unauthorized access and abuse</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">3.6.4 Data Processing</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>AI/ML processing uses aggregated and anonymized data where possible</li>
          <li>
            Personal information is processed securely and in accordance with this Privacy Policy
          </li>
          <li>You can opt-out of certain AI features through account settings (where available)</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.7 Automated Decision-Making</h3>
        <p>We may use automated decision-making processes for:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">3.7.1 Fraud Detection and Prevention</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Automated risk assessment for transactions</li>
          <li>Fraud scoring for referral program activities</li>
          <li>Automated blocking of suspicious activities</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">3.7.2 Service Personalization</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Automated content recommendations</li>
          <li>Personalized feature suggestions</li>
          <li>Automated categorization of transactions</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">3.7.3 Your Rights</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>You have the right to request human review of automated decisions</li>
          <li>You can object to automated processing (where applicable)</li>
          <li>Contact support@tellerwand.com to request review of automated decisions</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">4. How We Share Your Information</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">
          4.1 We Do NOT Sell Your Personal Information
        </h3>
        <p>
          <strong>
            TellerWand does NOT sell, rent, or trade your personal information to third parties for
            their marketing purposes.
          </strong>
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.2 Service Providers</h3>
        <p>
          We may share your information with trusted service providers who assist us in operating
          our Service:
        </p>

        <h4 className="text-base font-semibold mt-3 mb-2">4.2.1 Infrastructure Providers</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Supabase:</strong> Database and authentication services
          </li>
          <li>
            <strong>DigitalOcean:</strong> Cloud hosting and storage
          </li>
          <li>
            <strong>Cloudflare:</strong> CDN and security services
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">4.2.2 Payment Processors</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Cashfree:</strong> Payment processing and billing
          </li>
          <li>Other payment gateway providers</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">4.2.3 Analytics Providers</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Google Analytics:</strong> Usage analytics and insights
          </li>
          <li>
            <strong>OpenReplay:</strong> Session replay and monitoring
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">4.2.4 Communication Services</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Email service providers</li>
          <li>Notification services</li>
          <li>Customer support platforms</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">4.2.5 Security Services</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Security monitoring and threat detection</li>
          <li>Fraud prevention services</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.3 Legal Requirements</h3>
        <p>We may disclose your information if required by law or in response to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Court orders or legal processes</li>
          <li>Government requests</li>
          <li>Regulatory investigations</li>
          <li>Legal compliance requirements</li>
          <li>Protection of rights and safety</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.4 Business Transfers</h3>
        <p>
          In the event of a merger, acquisition, or sale of assets, your information may be
          transferred to the acquiring entity, subject to the same privacy protections.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.5 With Your Consent</h3>
        <p>
          We may share your information with third parties when you explicitly consent to such
          sharing.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.6 Aggregated and Anonymized Data</h3>
        <p>
          We may share aggregated, anonymized, or de-identified information that cannot be used to
          identify you individually for:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Research and analytics</li>
          <li>Business intelligence</li>
          <li>Industry reports</li>
          <li>Marketing purposes</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.7 Family Features (Premium)</h3>
        <p>If you use Family Finance Management features:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Family members you invite can see shared financial information</li>
          <li>You control what information is shared with family members</li>
          <li>Family member data is subject to the same privacy protections</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">5. Data Security</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">5.1 Security Measures</h3>
        <p>We implement industry-standard security measures to protect your information:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">5.1.1 Encryption</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>256-bit AES encryption</strong> for data at rest
          </li>
          <li>
            <strong>TLS/SSL encryption</strong> for data in transit
          </li>
          <li>End-to-end encryption for sensitive financial data</li>
          <li>Encrypted password storage (hashing with bcrypt)</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">5.1.2 Access Controls</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Role-based access control (RBAC)</li>
          <li>Multi-factor authentication (MFA) support</li>
          <li>Regular access reviews and audits</li>
          <li>Principle of least privilege</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">5.1.3 Infrastructure Security</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Secure cloud infrastructure (DigitalOcean)</li>
          <li>Regular security updates and patches</li>
          <li>Network security and firewalls</li>
          <li>DDoS protection (Cloudflare)</li>
          <li>Intrusion detection and prevention</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">5.1.4 Data Protection</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Regular security audits</li>
          <li>Vulnerability assessments</li>
          <li>Penetration testing</li>
          <li>Security monitoring and logging</li>
          <li>Incident response procedures</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">5.2 Security Certifications</h3>
        <p>We maintain compliance with:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>ISO 27001:</strong> Information Security Management
          </li>
          <li>
            <strong>SOC 2 Type II:</strong> Security, Availability, and Confidentiality
          </li>
          <li>Industry best practices and standards</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">5.3 Your Responsibility</h3>
        <p>While we implement strong security measures, you are also responsible for:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Using a strong, unique password</li>
          <li>Enabling multi-factor authentication (MFA)</li>
          <li>Keeping your login credentials secure</li>
          <li>Not sharing your account with others</li>
          <li>Logging out from shared devices</li>
          <li>Reporting suspicious activity immediately</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">5.4 Data Breach Notification</h3>
        <p>In the event of a data breach that may affect your personal information:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>We will notify affected users within 72 hours (as required by GDPR)</li>
          <li>We will notify relevant authorities as required by law</li>
          <li>We will provide clear information about the breach and steps to protect yourself</li>
        </ul>

        {/* PART 3: Sections 6-10 - Data Retention, Rights, Children, International, Third-Party */}
        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">6. Data Retention</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">6.1 Retention Periods</h3>
        <p>We retain your information for as long as necessary to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Provide our Service to you</li>
          <li>Comply with legal obligations</li>
          <li>Resolve disputes</li>
          <li>Enforce our agreements</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">6.2 Specific Retention Periods</h3>

        <h4 className="text-base font-semibold mt-3 mb-2">6.2.1 Account Data</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Active Accounts:</strong> Retained while your account is active
          </li>
          <li>
            <strong>Deletion requests:</strong> 7-day grace period during which you may cancel in
            Settings or Account
          </li>
          <li>
            <strong>After grace:</strong> Account is archived (soft-deleted) with up to 1 year
            recovery for support
          </li>
          <li>
            <strong>Archived accounts:</strong> Personal identifiers are removed from active
            systems; recovery data is retained per our deletion policy
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">6.2.2 Financial Data</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Transaction and payment records:</strong> Pseudonymized statutory ledger
            retained for 7 years (tax and legal compliance in India)
          </li>
          <li>
            <strong>Investment data:</strong> Retained while your account is active; archived with
            soft-deleted accounts
          </li>
          <li>
            <strong>After erasure:</strong> Financial identifiers are not retained in active product
            tables; ledger entries use provider payment IDs only (no email or name)
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">6.2.3 Communication Data</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Support Requests:</strong> Retained for 3 years
          </li>
          <li>
            <strong>Email Communications:</strong> Retained for 3 years
          </li>
          <li>
            <strong>Blog Comments:</strong> Retained while the blog post exists
          </li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">6.2.4 Log and Audit Data</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Platform audit log (hot):</strong> 12 months in our primary database (Mumbai
            region)
          </li>
          <li>
            <strong>Platform audit log (cold archive):</strong> Up to 3 years total in private
            encrypted object storage (India region)
          </li>
          <li>
            <strong>After account erasure:</strong> Audit entries are pseudonymized (subject tokens;
            no plaintext email, name, or amounts)
          </li>
          <li>
            <strong>Application error logs:</strong> Retained for up to 30 days
          </li>
          <li>
            <strong>Security event logs:</strong> Retained for up to 1 year
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">6.3 Data Deletion</h3>
        <p>When you request account deletion:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>You receive a 7-day grace period to cancel the request in Settings or Account</li>
          <li>
            After grace, your account is soft-deleted (archived) with up to 1 year recovery for
            support
          </li>
          <li>
            Statutory payment records are retained for 7 years in a pseudonymized ledger (provider
            order/payment IDs only)
          </li>
          <li>
            Platform audit logs are retained for up to 3 years with pseudonymization after erasure
          </li>
          <li>Aggregated, anonymized analytics may be retained where permitted by law</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">7. Your Rights and Choices</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.1 Access Rights</h3>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Access:</strong> Request a copy of your personal information
          </li>
          <li>
            <strong>Review:</strong> Review the personal information we hold about you
          </li>
          <li>
            <strong>Export:</strong> Export your data in a machine-readable format
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.2 Correction Rights</h3>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Update:</strong> Correct inaccurate or incomplete information
          </li>
          <li>
            <strong>Modify:</strong> Update your personal information through account settings
          </li>
          <li>
            <strong>Request Correction:</strong> Request correction of data we hold about you
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.3 Deletion Rights</h3>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Delete Account:</strong> Request deletion of your account and personal
            information
          </li>
          <li>
            <strong>Delete Specific Data:</strong> Request deletion of specific data points
          </li>
          <li>
            <strong>Right to be Forgotten:</strong> Request erasure of your personal information
            (GDPR)
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.4 Data Portability</h3>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Export Data:</strong> Receive your data in a structured, commonly used format
          </li>
          <li>
            <strong>Transfer Data:</strong> Transfer your data to another service provider
          </li>
          <li>
            <strong>Data Portability:</strong> Request transfer of your data (GDPR)
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.5 Opt-Out Rights</h3>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Marketing Communications:</strong> Opt-out of marketing emails (unsubscribe link
            in emails)
          </li>
          <li>
            <strong>Cookies:</strong> Manage cookie preferences through browser settings
          </li>
          <li>
            <strong>Analytics:</strong> Opt-out of analytics tracking (where available)
          </li>
          <li>
            <strong>Data Processing:</strong> Object to certain data processing activities
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.6 Restriction Rights</h3>
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Restrict Processing:</strong> Request restriction of data processing (GDPR)
          </li>
          <li>
            <strong>Object to Processing:</strong> Object to processing of your personal information
          </li>
          <li>
            <strong>Withdraw Consent:</strong> Withdraw consent for data processing (where
            applicable)
          </li>
          <li>
            <strong>Object to Automated Decision-Making:</strong> Request human review of automated
            decisions (GDPR)
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.7 How to Exercise Your Rights</h3>
        <p>To exercise your rights, please:</p>
        <ol className="list-decimal pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Email:</strong> Send a request to support@tellerwand.com
          </li>
          <li>
            <strong>Subject Line:</strong> Include "Privacy Request" and specify the right you wish
            to exercise
          </li>
          <li>
            <strong>Verification:</strong> We may require identity verification for security
          </li>
          <li>
            <strong>Response Time:</strong> We will respond within 30 days (or as required by
            applicable law)
          </li>
        </ol>

        <h3 className="text-lg font-semibold mt-4 mb-2">7.8 Non-Discrimination</h3>
        <p>We will not discriminate against you for exercising your privacy rights.</p>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">8. Children's Privacy</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">8.1 Age Requirement</h3>
        <p>
          Our Service is not intended for children under the age of 18 (or the age of majority in
          your jurisdiction). We do not knowingly collect personal information from children.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">8.2 Parental Consent</h3>
        <p>
          If you are a parent or guardian and believe your child has provided us with personal
          information, please contact us immediately at support@tellerwand.com.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">8.3 Removal of Children's Data</h3>
        <p>
          If we become aware that we have collected personal information from a child without
          parental consent, we will:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Delete the information immediately</li>
          <li>Terminate the child's account (if applicable)</li>
          <li>Notify the parent or guardian (if contact information is available)</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">9. International Data Transfers</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">9.1 Data Storage Locations</h3>
        <p>Your information may be stored and processed in:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>India:</strong> Primary data storage and processing
          </li>
          <li>
            <strong>United States:</strong> Some service providers (Supabase, DigitalOcean,
            Cloudflare)
          </li>
          <li>
            <strong>European Union:</strong> Some service providers (where applicable)
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">9.2 Transfer Safeguards</h3>
        <p>When transferring data internationally, we ensure:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Adequate Safeguards:</strong> Standard Contractual Clauses (SCCs) where required
          </li>
          <li>
            <strong>Data Protection:</strong> Compliance with applicable data protection laws
          </li>
          <li>
            <strong>Security Measures:</strong> Same security standards regardless of location
          </li>
          <li>
            <strong>Legal Compliance:</strong> Adherence to GDPR, CCPA, and other applicable
            regulations
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">9.3 Your Consent</h3>
        <p>
          By using our Service, you consent to the transfer of your information to countries that
          may have different data protection laws than your country of residence.
        </p>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">10. Third-Party Services</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">10.1 Third-Party Links</h3>
        <p>
          Our Service may contain links to third-party websites or services. We are not responsible
          for the privacy practices of third parties. We encourage you to review their privacy
          policies.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">10.2 Third-Party Integrations</h3>
        <p>We may integrate with third-party services for:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Payment processing</li>
          <li>Analytics</li>
          <li>Authentication</li>
          <li>Communication</li>
          <li>Storage</li>
        </ul>
        <p>
          These third parties have their own privacy policies governing data collection and use.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">10.3 Social Media</h3>
        <p>If you connect your account with social media services:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>We may receive information from those services</li>
          <li>Your use of social media features is subject to their privacy policies</li>
          <li>You can disconnect social media accounts at any time</li>
        </ul>

        {/* PART 4: Sections 11-20 - Cookies, DNT, Regional Rights, Contact, etc. */}
        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">11. Cookies and Tracking Technologies</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.1 Types of Cookies We Use</h3>

        <h4 className="text-base font-semibold mt-3 mb-2">11.1.1 Essential Cookies</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Required for Service functionality</li>
          <li>Cannot be disabled</li>
          <li>Include authentication and security cookies</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">11.1.2 Analytics Cookies</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Help us understand Service usage</li>
          <li>Improve user experience</li>
          <li>Can be disabled through browser settings</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">11.1.3 Preference Cookies</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Remember your settings</li>
          <li>Personalize your experience</li>
          <li>Can be disabled through browser settings</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">11.1.4 Marketing Cookies</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Used for advertising and referrals</li>
          <li>Track campaign effectiveness</li>
          <li>Can be disabled through browser settings</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.2 Managing Cookies</h3>
        <p>You can manage cookies through:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Browser Settings:</strong> Most browsers allow you to control cookies
          </li>
          <li>
            <strong>Cookie Preferences:</strong> Manage preferences in account settings (where
            available)
          </li>
          <li>
            <strong>Opt-Out Tools:</strong> Use industry opt-out tools for advertising cookies
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.3 Impact of Disabling Cookies</h3>
        <p>Disabling certain cookies may:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Affect Service functionality</li>
          <li>Limit personalized features</li>
          <li>Impact user experience</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">12. Do Not Track Signals</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">12.1 Browser Do Not Track</h3>
        <p>
          Some browsers include "Do Not Track" (DNT) features. We respect DNT signals and do not
          track users who have enabled DNT, except as required for Service functionality.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">12.2 Limitations</h3>
        <p>DNT signals may not affect:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Essential Service functionality</li>
          <li>Security and fraud prevention</li>
          <li>Legal compliance requirements</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">13. California Privacy Rights (CCPA)</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">13.1 California Consumer Rights</h3>
        <p>If you are a California resident, you have additional rights under CCPA:</p>

        <h4 className="text-base font-semibold mt-3 mb-2">13.1.1 Right to Know</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>What personal information we collect</li>
          <li>How we use your personal information</li>
          <li>Categories of third parties with whom we share information</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">13.1.2 Right to Delete</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Request deletion of your personal information</li>
          <li>Subject to certain exceptions (legal compliance, etc.)</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">13.1.3 Right to Opt-Out</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Opt-out of sale of personal information (we do not sell personal information)</li>
          <li>Opt-out of sharing for cross-context behavioral advertising</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">13.1.4 Right to Non-Discrimination</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>We will not discriminate for exercising CCPA rights</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">13.2 Exercising CCPA Rights</h3>
        <p>To exercise your CCPA rights:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Email: support@tellerwand.com</li>
          <li>Subject: "CCPA Privacy Request"</li>
          <li>Include: Your name, email, and specific request</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">14. European Privacy Rights (GDPR)</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">14.1 GDPR Rights</h3>
        <p>
          If you are in the European Economic Area (EEA), you have additional rights under GDPR:
        </p>

        <h4 className="text-base font-semibold mt-3 mb-2">14.1.1 Right of Access</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Obtain confirmation of data processing</li>
          <li>Access your personal information</li>
          <li>Receive a copy of your data</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">14.1.2 Right to Rectification</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Correct inaccurate data</li>
          <li>Complete incomplete data</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">
          14.1.3 Right to Erasure (Right to be Forgotten)
        </h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Request deletion of your personal information</li>
          <li>Subject to legal retention requirements</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">14.1.4 Right to Restrict Processing</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Request restriction of data processing</li>
          <li>Under certain circumstances</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">14.1.5 Right to Data Portability</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Receive your data in a structured format</li>
          <li>Transfer data to another service</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">14.1.6 Right to Object</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Object to processing of personal information</li>
          <li>Object to direct marketing</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">14.1.7 Right to Withdraw Consent</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Withdraw consent for data processing</li>
          <li>Where processing is based on consent</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">
          14.1.8 Right to Object to Automated Decision-Making
        </h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Object to automated decision-making, including profiling</li>
          <li>Request human intervention in automated decisions</li>
          <li>Express your point of view and contest automated decisions</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">14.2 Legal Basis for Processing</h3>
        <p>We process your personal information based on:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Consent:</strong> When you provide explicit consent
          </li>
          <li>
            <strong>Contract:</strong> To fulfill our contract with you
          </li>
          <li>
            <strong>Legal Obligation:</strong> To comply with legal requirements
          </li>
          <li>
            <strong>Legitimate Interests:</strong> For our legitimate business interests
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">14.3 Data Protection Officer</h3>
        <p>For GDPR-related inquiries, contact:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Email:</strong> support@tellerwand.com
          </li>
          <li>
            <strong>Subject:</strong> "GDPR Privacy Request"
          </li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">15. Indian Privacy Rights (DPDPA)</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">15.1 DPDPA Rights</h3>
        <p>
          If you are in India, you have rights under the Digital Personal Data Protection Act
          (DPDPA):
        </p>

        <h4 className="text-base font-semibold mt-3 mb-2">15.1.1 Right to Information</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Know what personal data is being processed</li>
          <li>Purpose of processing</li>
          <li>Entities with whom data is shared</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">15.1.2 Right to Correction</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Correct inaccurate personal data</li>
          <li>Update incomplete data</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">15.1.3 Right to Erasure</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Request deletion of personal data</li>
          <li>Subject to legal retention requirements</li>
        </ul>

        <h4 className="text-base font-semibold mt-3 mb-2">15.1.4 Right to Grievance Redressal</h4>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>File complaints about data processing</li>
          <li>Seek resolution of privacy concerns</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">15.2 Exercising DPDPA Rights</h3>
        <p>You may exercise your DPDPA rights in the app or by email:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Access &amp; portability:</strong> Settings → Your data → Export My Data (MFA
            required)
          </li>
          <li>
            <strong>Erasure:</strong> Settings or Account → Request Account Deletion (7-day grace,
            cancellable; MFA required)
          </li>
          <li>
            <strong>Correction:</strong> Update your profile in Account settings
          </li>
          <li>
            <strong>Policy acknowledgement:</strong> Material privacy policy updates require in-app
            acknowledgement (with MFA)
          </li>
          <li>
            <strong>Email:</strong> support@tellerwand.com — Subject: &quot;DPDPA Privacy
            Request&quot;
          </li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Primary data processing is in India (Mumbai / ap-south-1). Platform audit logs are
          mandatory for security and compliance; regular administrators see masked identifiers;
          super administrators may access full audit detail under strict controls.
        </p>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">16. Marketing Communications</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">16.1 Opt-In Consent</h3>
        <p>
          We only send marketing communications with your explicit consent. You can opt-in when:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Creating an account</li>
          <li>Subscribing to newsletters</li>
          <li>Participating in promotions</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">16.2 Opt-Out</h3>
        <p>You can opt-out of marketing communications by:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Clicking "Unsubscribe" in marketing emails</li>
          <li>Updating preferences in account settings</li>
          <li>Contacting support@tellerwand.com</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">16.3 Service Communications</h3>
        <p>You cannot opt-out of:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Service-related notifications</li>
          <li>Important account updates</li>
          <li>Security alerts</li>
          <li>Legal notices</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">17. Changes to This Privacy Policy</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">17.1 Policy Updates</h3>
        <p>We may update this Privacy Policy from time to time to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Reflect changes in our practices</li>
          <li>Comply with legal requirements</li>
          <li>Improve clarity and transparency</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">17.2 Notification of Changes</h3>
        <p>We will notify you of material changes by:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Email:</strong> Sending an email to your registered email address
          </li>
          <li>
            <strong>Website:</strong> Posting a notice on our Service
          </li>
          <li>
            <strong>In-App:</strong> Displaying a notification in the application
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">17.3 Effective Date</h3>
        <p>
          The "Last Updated" date at the top of this Privacy Policy indicates when it was last
          revised.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">17.4 Continued Use</h3>
        <p>
          Your continued use of our Service after changes become effective constitutes acceptance of
          the updated Privacy Policy.
        </p>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">18. Contact Information</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">18.1 Privacy Inquiries</h3>
        <p>
          For questions, concerns, or requests regarding this Privacy Policy or your personal
          information:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Email:</strong> support@tellerwand.com
          </li>
          <li>
            <strong>Subject Line:</strong> "Privacy Inquiry" or "Privacy Request"
          </li>
          <li>
            <strong>Response Time:</strong> We aim to respond within 30 days (or as required by
            applicable law)
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">18.2 Data Protection Officer</h3>
        <p>For GDPR-related inquiries:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Email:</strong> support@tellerwand.com
          </li>
          <li>
            <strong>Subject:</strong> "GDPR Inquiry"
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">18.3 General Contact</h3>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Website:</strong> https://tellerwand.com
          </li>
          <li>
            <strong>Contact Page:</strong> https://tellerwand.com/contact-us
          </li>
          <li>
            <strong>Support Hours:</strong> Monday to Friday, 9:00 AM to 6:00 PM IST
          </li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">19. Additional Information</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.1 Data Controller</h3>
        <p>
          <strong>TellerWand Inc.</strong> is the data controller responsible for your personal
          information.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.2 Data Processor</h3>
        <p>
          We may use third-party data processors who process data on our behalf under strict
          contractual obligations.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.3 Supervisory Authority</h3>
        <p>
          If you are in the EEA and have concerns about our data processing, you have the right to
          lodge a complaint with your local data protection supervisory authority.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.4 Governing Law</h3>
        <p>
          This Privacy Policy is governed by the laws of India, with jurisdiction in Bangalore,
          Karnataka, India.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.5 Data Processing Agreements</h3>
        <p>
          We enter into Data Processing Agreements (DPAs) with all third-party service providers who
          process personal information on our behalf. These agreements ensure that:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Third parties process data only for specified purposes</li>
          <li>Appropriate security measures are maintained</li>
          <li>Data is not used for other purposes</li>
          <li>Data is deleted when no longer needed</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.6 Research and Development</h3>
        <p>We may use aggregated, anonymized data for:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Research and development purposes</li>
          <li>Improving our Service and features</li>
          <li>Industry analysis and benchmarking</li>
          <li>Academic research (with appropriate safeguards)</li>
        </ul>
        <p>
          No personally identifiable information is used in research without your explicit consent.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.7 Account Recovery and Data Access</h3>
        <p>During account recovery processes:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>We may request additional verification information</li>
          <li>Recovery data is used solely for account restoration</li>
          <li>Recovery information is securely stored and deleted after successful recovery</li>
          <li>We follow strict verification procedures to protect your account</li>
        </ul>

        <hr className="my-6" />

        <h2 className="text-xl font-semibold mt-6 mb-3">20. Acceptance</h2>
        <p>
          By using our Service, you acknowledge that you have read, understood, and agree to be
          bound by this Privacy Policy.
        </p>

        <hr className="my-6" />

        <p className="text-sm text-muted-foreground mt-6">
          <strong>TellerWand Inc.</strong>
          <br />
          <strong>Last Updated:</strong> January 5, 2026
          <br />
          <strong>Version:</strong> 1.0
        </p>

        <p className="text-xs text-muted-foreground mt-4 italic">
          Note: This Privacy Policy is comprehensive and designed to comply with GDPR, CCPA, DPDPA,
          and other applicable data protection regulations. For specific legal advice, please
          consult with a qualified legal professional.
        </p>
      </CardContent>
    </Card>
  );

  if (user) {
    return (
      <RootLayout>
        <Helmet>
          <title>Privacy Policy | TellerWand</title>
          <meta
            name="description"
            content="TellerWand Privacy Policy - Learn how we collect, use, and protect your personal information. Compliant with GDPR, CCPA, DPDPA, ISO 27001, and SOC 2."
          />
        </Helmet>
        <div className="container max-w-4xl mx-auto p-4 sm:p-6">
          <Button variant="outline" asChild className="mb-4 md:mb-6">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </Button>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Privacy Policy</h1>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6 flex items-start">
            <Shield className="text-blue-600 h-4 w-4 sm:h-5 sm:w-5 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h2 className="font-medium text-blue-800 text-sm sm:text-base">
                GDPR and Data Protection
              </h2>
              <p className="text-xs sm:text-sm text-blue-700">
                This privacy policy has been updated to comply with GDPR, ISO 27001, SOC 2, CCPA,
                DPDPA, and other applicable data protection regulations.
              </p>
            </div>
          </div>

          {privacyContent}
        </div>
      </RootLayout>
    );
  }

  return (
    <RootLayout>
      <Helmet>
        <title>Privacy Policy | TellerWand</title>
        <meta
          name="description"
          content="TellerWand Privacy Policy - Learn how we collect, use, and protect your personal information. Compliant with GDPR, CCPA, DPDPA, ISO 27001, and SOC 2."
        />
      </Helmet>
      <div className="bg-gradient-to-b from-white to-gray-50">
        <div className="container max-w-4xl mx-auto p-4 sm:p-6">
          <Button variant="outline" asChild className="mb-4 md:mb-6">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </Button>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Privacy Policy</h1>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6 flex items-start">
            <Shield className="text-blue-600 h-4 w-4 sm:h-5 sm:w-5 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h2 className="font-medium text-blue-800 text-sm sm:text-base">
                GDPR and Data Protection
              </h2>
              <p className="text-xs sm:text-sm text-blue-700">
                This privacy policy has been updated to comply with GDPR, ISO 27001, SOC 2, CCPA,
                DPDPA, and other applicable data protection regulations.
              </p>
            </div>
          </div>

          {privacyContent}
        </div>
      </div>
    </RootLayout>
  );
}
